import React, { Fragment, useMemo } from 'react';

import { cn } from '@blockworks/ui/utils';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';

export type SingleSelectInputOption = {
    id: string;
    name: string;
    imageUrl?: string;
};

type SelectBoxProps = {
    // Data
    options: SingleSelectInputOption[];
    value?: SingleSelectInputOption | null;
    label: string;
    placeholder?: string;
    // Functions
    onChange: (value?: any) => void;
    className?: string;
};

const SingleSelectDropdown = (props: SelectBoxProps) => {
    const { options, value, label, className, onChange, placeholder } = props;
    const sortedOptions = useMemo(() => options.sort((a, b) => a.name?.localeCompare(b.name)), [options]);

    return (
        <Listbox value={value} onChange={onChange}>
            {({ open }) => (
                <div className="flex flex-col w-full gap-1">
                    {label && <Listbox.Label className="text-sm text-left text-gray-700">{label}</Listbox.Label>}
                    <div className="relative">
                        <Listbox.Button
                            className={cn(
                                'border relative w-full cursor-default py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm focus:border-primary ',
                                !open
                                    ? 'border-gray-200 bg-white hover:border-gray-300 '
                                    : 'border-primary ring-1 ring-primary outline-none bg-gray-50 hover:border-primary',
                                className,
                            )}
                        >
                            <span className="flex items-center w-full h-6 relative">
                                {value ? (
                                    <>
                                        {value.imageUrl && (
                                            <img
                                                src={value.imageUrl}
                                                alt=""
                                                className="h-6 w-6 flex-shrink-0 rounded-full"
                                            />
                                        )}
                                        <span className={cn('block truncate', value.imageUrl && 'ml-2')}>
                                            {value.name}
                                        </span>
                                        {onChange && (
                                            <a
                                                onClick={ev => {
                                                    onChange(null);
                                                    ev.stopPropagation();
                                                }}
                                                className="absolute -right-2 cursor-pointer hover:text-primary p-0.5 bg-gray-100 border border-transparent hover:border-gray-200 active:border-gray-300 active:bg-gray-50 text-primary hover:bg-gray-100"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth={1.5}
                                                    stroke="currentColor"
                                                    className="w-4 h-4"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M6 18L18 6M6 6l12 12"
                                                    />
                                                </svg>
                                            </a>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-sm lg:text-md text-gray-400 pointer-events-none inset-y-0 p-0.5 flex items-center w-full truncate">
                                        {placeholder ?? ''}
                                    </p>
                                )}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-1 ml-1 flex items-center pr-2">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="h-5 w-5 text-gray-400"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                                    />
                                </svg>
                            </span>
                        </Listbox.Button>

                        <Transition
                            show={open}
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Listbox.Options
                                className={cn(
                                    'absolute z-10 mt-1 max-h-56 w-full overflow-auto py-1 text-sm lg:text-base shadow-xs ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
                                    open ? 'bg-gray-50' : 'bg-white',
                                )}
                            >
                                {sortedOptions.map(item => (
                                    <Listbox.Option
                                        key={item.id}
                                        className={({ active }) =>
                                            cn(
                                                active ? 'text-white bg-primary' : 'text-gray-900',
                                                'relative cursor-default select-none py-2 pl-3 pr-9',
                                            )
                                        }
                                        value={item}
                                    >
                                        {({ selected, active }) => (
                                            <>
                                                <div className="flex items-center">
                                                    {item.imageUrl && (
                                                        <img
                                                            src={item.imageUrl}
                                                            alt=""
                                                            className="h-6 w-6 flex-shrink-0 rounded-full bg-white ring-1 ring-white"
                                                        />
                                                    )}
                                                    <span
                                                        className={cn(
                                                            selected ? 'font-semibold' : 'font-normal',
                                                            'ml-3 block truncate',
                                                        )}
                                                    >
                                                        {item.name}
                                                    </span>
                                                </div>
                                                {selected ? (
                                                    <span
                                                        className={cn(
                                                            active ? 'text-white' : 'text-purple-600',
                                                            'absolute inset-y-0 right-0 flex items-center pr-4',
                                                        )}
                                                    >
                                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                    </span>
                                                ) : null}
                                            </>
                                        )}
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </Transition>
                    </div>
                </div>
            )}
        </Listbox>
    );
};

export default SingleSelectDropdown;
